// Identity and time shared by sim, hosts and client (docs/ARCHITECTURE.md §2, §3).

import { GAME_MODES, type BuildingKey, type StrikeKey, type TypeId, type UnitKey } from '@taxwar/content'
import { InvariantViolation } from './errors'

declare const simIdBrand: unique symbol
declare const playerIdBrand: unique symbol
declare const cityIndexBrand: unique symbol
declare const matchSeedBrand: unique symbol

/** Sim-owned gameplay identity (MatchState.nextSimId). Never an ESEngine entity.id. 0 = none. */
export type SimId = number & { readonly [simIdBrand]: true }
export const NO_SIM_ID = 0 as SimId

/**
 * A belligerent's seat in one war: 0 … belligerents − 1, in SkirmishSetup.belligerents order. Neutral ownership is
 * NEUTRAL_OWNER, which is not a PlayerId. Who is hostile to whom is the sim's Diplomacy, never seat arithmetic.
 */
export type PlayerId = number & { readonly [playerIdBrand]: true }
/** Owner codes are synced as int8 (Owner.player, CityNode.captureBy): no war has more seats than this. */
export const MAX_PLAYERS = 16
export const NEUTRAL_OWNER = -1
/** Owner component value: a seat or neutral. */
export type OwnerValue = PlayerId | typeof NEUTRAL_OWNER

export function isPlayerId(owner: number): owner is PlayerId {
  return Number.isInteger(owner) && owner >= 0 && owner < MAX_PLAYERS
}

/** A seat index from trusted code (a loop over a war's seats); anything else is a programmer error. */
export function playerIdOf(seat: number): PlayerId {
  if (!isPlayerId(seat)) throw new InvariantViolation(`seat ${seat} is not a PlayerId`)
  return seat
}

/** Every seat of a war with `count` belligerents, ascending. */
export function playerIdsOf(count: number): readonly PlayerId[] {
  if (!Number.isInteger(count) || count < 1 || count > MAX_PLAYERS) throw new InvariantViolation(`a war cannot have ${count} seats`)
  return Array.from({ length: count }, (_, seat) => seat as PlayerId)
}

/** Index of a city in its WarMap (stable for the whole match). */
export type CityIndex = number & { readonly [cityIndexBrand]: true }
/** Mobility.goalCity / GraphTraversal value meaning "no city". */
export const NO_GOAL_CITY = -1

/** uint32 match seed; the whole war (map + RNG) is reproducible from it and the command log. */
export type MatchSeed = number & { readonly [matchSeedBrand]: true }

export type UnitTypeId = TypeId<UnitKey>
export type BuildingTypeId = TypeId<BuildingKey>
export type StrikeTypeId = TypeId<StrikeKey>

/** Sim tick rate; the host is the only clock owner. */
export const SIM_HZ = 20
export const FIXED_DT = 1 / SIM_HZ

export function secondsToTicks(seconds: number): number {
  return Math.round(seconds * SIM_HZ)
}

// Every mode's seat range must fit the synced owner codes.
for (const [mode, def] of Object.entries(GAME_MODES)) {
  if (def.belligerentsMax > MAX_PLAYERS) throw new InvariantViolation(`mode ${mode} allows ${def.belligerentsMax} seats; owner codes hold ${MAX_PLAYERS}`)
}
